/**
 * REACTION — eine kurze Nachricht an den Betreiber (Jan 03.10.2026, gedacht
 * vor allem für die Postkarte): oben die Aufforderung, darunter ein Textfeld
 * (höchstens 144 Zeichen, ein Emoji zählt als ein Zeichen), eine Reihe Emojis
 * zum Antippen und „Senden". Danach steht nur der Danke-Satz da — was andere
 * geschrieben haben, sieht der Leser nie; die Liste gibt es nur im Editor.
 *
 * EINE Nachricht je Gerät: Echte Leser merken sich das Senden (wie bei VOTE)
 * und sehen beim Wiederkommen gleich den Danke-Satz; der Server ersetzt eine
 * erneute Nachricht desselben Geräts.
 *
 * TIPPEN HÄLT DEN CTA FEST (`onFesthalten`): Öffnet das Handy die Tastatur,
 * verschiebt es oft die Seite — und weil der Scrolly am Scrollen hängt, damit
 * auch die Slide. Ohne Festhalten glitte die Reaktion mitten im Tippen
 * hinaus (RunningCtaStapel hält sie, solange das Feld den Fokus hat).
 *
 * Im EDITOR ist sie bedienbar, damit man beide Zustände sieht; nichts wird
 * gemerkt oder gesendet, und ↺ setzt sie zurück.
 */
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, FunctionComponent } from "react";
import type { Color, CtaAbgabe, ResolvedReaction, ResolvedRunningCta } from "@scrolly/post-types";
// Gewählter Schnitt = Grundtext (Textfeld, Zähler); Frage und Danke bleiben
// kräftiger, nie dünner (Jan 07.10.2026, s. schrift-staerke.ts)
import { betonterSchnitt, grundSchnitt } from "@scrolly/post-types";
import {
    REAKTION_EMOJIS,
    REAKTION_MAX_ZEICHEN,
    reaktionBereinigen,
    reaktionKuerzen,
    reaktionZeichen,
} from "@scrolly/post-types";
import { ctaGeraeteId, leseGemerkt, merke, ZUKLAPPEN_NACH_MS } from "./ctaMerken";
import DankeZeile from "./DankeZeile";

const farbe = (c: Color, alpha = 1): string => `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a * alpha})`;

interface Props {
    strip: ResolvedRunningCta;
    reaktion: ResolvedReaction;
    editorMode?: boolean;
    /** Gesetzt = echter Leser: Nachricht merken und senden. Fehlt = Vorschau. */
    onCtaAntwort?: (abgabe: CtaAbgabe) => void;
    /** Solange getippt wird, den CTA festhalten (s. Kopf). */
    onFesthalten?: (an: boolean) => void;
}

const RunningReaction: FunctionComponent<Props> = ({ strip, reaktion, editorMode, onCtaAntwort, onFesthalten }) => {
    // Nur echte Leser merken sich das Senden (Vorschauen bleiben frisch)
    const echterLeser = !editorMode && Boolean(onCtaAntwort);
    const [text, setText] = useState("");
    const [abgegeben, setAbgegeben] = useState(false);
    // Nach dem Senden kurz offen, dann zur schmalen Danke-Zeile (ZUKLAPPEN_NACH_MS)
    const [zu, setZu] = useState(false);
    const feld = useRef<HTMLTextAreaElement | null>(null);
    const festhalten = useRef(onFesthalten);
    festhalten.current = onFesthalten;
    // Schreibmarke nach einem eingefügten Emoji: gleich dahinter. Der neue Wert
    // schöbe sie sonst ans Ende (gemessen 03.10.2026) — wer weitertippte,
    // schrieb hinten weiter.
    const marke = useRef<number | null>(null);
    useEffect(() => {
        const el = feld.current;
        const pos = marke.current;
        marke.current = null;
        if (pos === null || !el || document.activeElement !== el) return;
        el.setSelectionRange(pos, pos);
    }, [text]);

    useEffect(() => {
        if (!abgegeben || zu) return undefined;
        const t = window.setTimeout(() => setZu(true), ZUKLAPPEN_NACH_MS);
        return () => window.clearTimeout(t);
    }, [abgegeben, zu]);

    // Leser: schon geschrieben? Dann gleich die schmale Danke-Zeile.
    useEffect(() => {
        if (!echterLeser) return;
        if (leseGemerkt(strip.id)) {
            setAbgegeben(true);
            setZu(true);
        }
    }, [echterLeser, strip.id]);

    // Nie festgehalten zurücklassen (Abbau mitten im Tippen)
    useEffect(() => () => festhalten.current?.(false), []);

    const nachricht = reaktionBereinigen(text);
    const bereit = nachricht.length > 0 && !abgegeben;

    const senden = () => {
        if (!bereit) return;
        setAbgegeben(true);
        festhalten.current?.(false);
        feld.current?.blur();
        if (echterLeser && onCtaAntwort) {
            merke(strip.id, [nachricht]);
            onCtaAntwort({ ctaId: strip.id, art: "reaction", antwort: [nachricht], geraet: ctaGeraeteId() });
        }
    };

    /** Emoji an der Schreibmarke einfügen — ohne das Feld zu fokussieren (sonst
     *  klappte am Handy für ein einzelnes Emoji die Tastatur auf). */
    const fuegeEin = (emoji: string) => {
        if (abgegeben) return;
        const el = feld.current;
        const imFeld = el !== null && typeof document !== "undefined" && document.activeElement === el;
        const von = imFeld ? el.selectionStart : text.length;
        const bis = imFeld ? el.selectionEnd : text.length;
        const neu = reaktionKuerzen(text.slice(0, von) + emoji + text.slice(bis));
        // voll (144): das Emoji passt nicht mehr hinein — nichts ändert sich
        if (neu === text) return;
        if (imFeld) marke.current = Math.min(von + emoji.length, neu.length);
        setText(neu);
    };

    const textFarbe = strip.textColor;
    const schrift: CSSProperties = strip.fontFamily ? { fontFamily: `'${strip.fontFamily}', sans-serif` } : {};
    const k = reaktion.knopf;
    const kontur = k.borderColor ? farbe(k.borderColor) : farbe(textFarbe);
    const danke = reaktion.danke || "Danke für deine Nachricht!";
    const zeichen = reaktionZeichen(text);
    const zuruecksetzen = () => {
        setText("");
        setAbgegeben(false);
        setZu(false);
    };

    if (zu) {
        return (
            <div style={{ width: "100%", color: farbe(textFarbe), fontSize: `${strip.fontSize}px`, ...schrift }}>
                <DankeZeile
                    text={danke}
                    staerke={betonterSchnitt(strip.fontWeight, 600)}
                    knopfGrund={farbe(textFarbe, 0.18)}
                    onZurueck={editorMode ? zuruecksetzen : undefined}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                width: "100%",
                maxWidth: "560px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                color: farbe(textFarbe),
                fontSize: `${strip.fontSize}px`,
                lineHeight: 1.3,
                ...schrift,
            }}
        >
            {reaktion.frage ? (
                <div style={{ fontWeight: betonterSchnitt(strip.fontWeight, 700), paddingRight: "28px" }}>
                    {reaktion.frage}
                </div>
            ) : null}

            <textarea
                ref={feld}
                value={text}
                rows={3}
                disabled={abgegeben}
                aria-label={reaktion.frage || "Nachricht"}
                placeholder={reaktion.platzhalter || "Deine Nachricht …"}
                onChange={(e) => setText(reaktionKuerzen(e.target.value))}
                onFocus={() => festhalten.current?.(true)}
                onBlur={() => festhalten.current?.(false)}
                style={{
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "none",
                    padding: "8px 10px",
                    // nur die Familie erben, NICHT `font: inherit` — Kurzform
                    // neben fontSize verträgt sich in React nicht (s. RunningCtaStrip)
                    fontFamily: "inherit",
                    // Felder erben keine Stärke — ausdrücklich (Regular wie bisher)
                    fontWeight: grundSchnitt(strip.fontWeight, 400),
                    // unter 16 px zoomt das iPhone beim Antippen ins Feld
                    fontSize: `${Math.max(16, strip.fontSize)}px`,
                    lineHeight: 1.35,
                    color: "inherit",
                    background: farbe(textFarbe, 0.1),
                    border: `1.5px solid ${farbe(textFarbe, 0.45)}`,
                    borderRadius: "8px",
                    outline: "none",
                    opacity: abgegeben ? 0.6 : 1,
                }}
            />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {REAKTION_EMOJIS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            disabled={abgegeben}
                            aria-label={`${emoji} einfügen`}
                            // mousedown nicht weitergeben: sonst verlöre das Feld
                            // den Fokus (und die Schreibmarke) vor dem Einfügen
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => fuegeEin(emoji)}
                            style={{
                                width: "36px",
                                height: "36px",
                                padding: 0,
                                border: "none",
                                borderRadius: "8px",
                                background: farbe(textFarbe, 0.1),
                                fontSize: "20px",
                                lineHeight: 1,
                                cursor: abgegeben ? "default" : "pointer",
                            }}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
                <span
                    aria-live="polite"
                    style={{
                        flexShrink: 0,
                        fontSize: "12px",
                        fontVariantNumeric: "tabular-nums",
                        fontWeight:
                            zeichen >= REAKTION_MAX_ZEICHEN
                                ? betonterSchnitt(strip.fontWeight, 700)
                                : grundSchnitt(strip.fontWeight, 400),
                        opacity: zeichen >= REAKTION_MAX_ZEICHEN ? 1 : 0.75,
                    }}
                >
                    {zeichen}/{REAKTION_MAX_ZEICHEN}
                </span>
            </div>

            {abgegeben ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: betonterSchnitt(strip.fontWeight, 600) }}>
                    <span>{danke}</span>
                </div>
            ) : (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                        type="button"
                        onClick={senden}
                        disabled={!bereit}
                        style={{
                            color: kontur,
                            border: `${k.borderWidth}px solid ${kontur}`,
                            borderRadius: `${k.radius}px`,
                            background: k.fillColor ? farbe(k.fillColor) : "transparent",
                            padding: "0.5em 1.4em",
                            fontWeight: k.fontWeight,
                            fontSize: "inherit",
                            cursor: bereit ? "pointer" : "default",
                            opacity: bereit ? 1 : 0.5,
                            ...(k.fontFamily ? { fontFamily: `'${k.fontFamily}', sans-serif` } : {}),
                        }}
                    >
                        {k.text}
                    </button>
                </div>
            )}
        </div>
    );
};

export default RunningReaction;
